/* eslint-disable react/prop-types */
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

const CurriculumCSE = ({ data, departmentName }) => {
  const [expandedSemester, setExpandedSemester] = useState(null);

  return (
    <div className="container p-8 mx-auto">
      <h2 className="mb-12 text-xl font-bold text-center md:text-2xl lg:text-4xl font-domine">
        {departmentName} Curriculum
      </h2>

      <div className="space-y-4">
        {data?.map((semester) => (
          <motion.div
            key={semester.courseMap.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="overflow-hidden border-b border-gray-200"
          >
            {/* Accordion Header */}
            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={() =>
                setExpandedSemester(
                  expandedSemester === semester.courseMap.id
                    ? null
                    : semester.courseMap.id
                )
              }
              className="flex items-center justify-between w-full px-4 py-4 transition-all duration-300 sm:px-6 bg-gradient-to-r from-white to-gray-50 hover:from-gray-50 hover:to-gray-100"
              aria-expanded={expandedSemester === semester.courseMap.id}
            >
              <div className="text-left">
                <motion.h2
                  className="text-base font-semibold text-transparent sm:text-lg bg-clip-text bg-gradient-to-r from-primary-600 to-primary-400"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  {semester.courseMap.area}
                </motion.h2>
                <motion.p
                  className="mt-1 text-xs text-gray-600 sm:text-sm"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  {semester.courseMap.number_of_courses} Courses •{" "}
                  {semester.courseMap.total_credits} Credits
                </motion.p>
              </div>
              <motion.div
                animate={{
                  rotate: expandedSemester === semester.courseMap.id ? 180 : 0,
                }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                className="flex-shrink-0 ml-2 text-gray-500"
              >
                <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5" />
              </motion.div>
            </motion.button>

            {/* Accordion Content */}
            <AnimatePresence>
              {expandedSemester === semester.courseMap.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 100, damping: 20 }}
                  className="overflow-hidden"
                >
                  <div className="overflow-x-auto">
                    <motion.table
                      className="min-w-full divide-y divide-gray-200"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.1 }}
                    >
                      <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                        <tr>
                          <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase sm:px-6">
                            Course Code
                          </th>
                          <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase sm:px-6">
                            Course Name
                          </th>
                          <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase sm:px-6">
                            Credits
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {semester.courses?.map((course, index) => (
                          <motion.tr
                            key={course.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            whileHover={{
                              scale: 1.01,
                              backgroundColor: "rgba(249, 250, 251, 0.5)",
                            }}
                            className="transition-all duration-200 transform"
                          >
                            <td className="px-4 py-4 sm:px-6 whitespace-nowrap">
                              <motion.span
                                className="text-sm font-medium text-primary-600"
                                whileHover={{ scale: 1.05 }}
                              >
                                {course.code}
                              </motion.span>
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-900 sm:px-6 whitespace-nowrap">
                              {course.name}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-500 sm:px-6 whitespace-nowrap">
                              {course.credit}
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </motion.table>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default CurriculumCSE;
